import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { take } from 'rxjs';
import { CameraState } from '../../connect-3d-ext/camera/camera.state';
import { RotatePanoramas } from '../../connect-3d-ext/host-3d.actions';
import { Host3dService } from '../../connect-3d-ext/host-3d.service';
import { ConnectService } from '../../connect/connect.service';
import { PatchScandataModel } from '../../scandata/scandata.actions';
import { ScandataModel } from '../../scandata/scandata.models';
import { calculateStationHeadingOffset } from '../../station/station-transform.models';
import { Station } from '../../station/station.models';
import { StationState } from '../../station/station.state';
import { openInNumark } from '../../utils/open-in-numark';
import { ScandataTreeComponent } from '../scandata-tree/scandata-tree.component';

@UntilDestroy()
@Component({
  selector: 'sd-scandata-tree-view',
  imports: [CommonModule, ScandataTreeComponent],
  templateUrl: './scandata-tree-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScandataTreeViewComponent {
  private readonly connectService = inject(ConnectService);
  private readonly host3dService = inject(Host3dService);
  private readonly store = inject(Store);

  readonly currentStation = toSignal(this.store.select(StationState.currentStation));
  readonly cameraProjection = toSignal(this.store.select(CameraState.projection));

  readonly scandata = input.required<ScandataModel[]>();
  readonly quotaExceeded = input(false);
  readonly hasRotatePanoramasFeature = input(false);
  readonly hasViewInNumarkFeature = input(false);
  readonly modelDetailClick = output<ScandataModel>();

  modelIconClick(scan: ScandataModel) {
    const show = !scan.showInScene;
    if (show) {
      this.host3dService.showScan(scan).pipe(take(1)).subscribe();
    } else {
      this.host3dService.hideScan(scan).pipe(take(1)).subscribe();
    }
  }

  modelNameClick(model: ScandataModel) {
    if (model.numberOfStations > 0) {
      this.toggleExpand(model);
    }
  }

  modelDetailClicked(model: ScandataModel) {
    this.modelDetailClick.emit(model);
  }

  modelFitToViewClicked(model: ScandataModel) {
    void this.connectService.fitToView([model.web3dId]);
  }

  modelRotatePanoramasClicked(scan: ScandataModel) {
    const currentHeadingOffset = calculateStationHeadingOffset(scan.stationTransform);
    this.store.dispatch(new RotatePanoramas(scan.id, currentHeadingOffset + 90));
  }

  toggleExpand(scan: ScandataModel) {
    const expand = !scan.expanded;

    this.store.dispatch(
      new PatchScandataModel({
        id: scan.id,
        expanded: expand,
      }),
    );

    if (expand) {
      this.host3dService.loadScanStations(scan).pipe(take(1)).subscribe();
    }
  }

  stationIconClick(station: Station) {
    this.host3dService.setCurrentStation(station).pipe(take(1)).subscribe();
  }

  stationNameClick(station: Station) {
    this.host3dService.setCurrentStation(station).pipe(take(1)).subscribe();
  }

  viewInNumarkClick(scan: ScandataModel) {
    openInNumark(this.store, [scan.id]);
  }
}
